<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import ModalBackdrop from '../ModalBackdrop.vue'
import Stepper from './Stepper.vue'
import AppButton from '@/components/Base/Buttons/AppButton.vue'
import AppButtonCircular from '@/components/Base/Buttons/AppButtonCircular.vue'
import { parseMarkdown } from '@/utils/helpers'
import { Checkbox } from '@easybell/caramel-vue3'

export type Step = {
  /** URL of the image */
  image: string
  title: string
  text: string
  /** Image background-position. Defaults to 'center' */
  imagePosition?: 'center' | 'top' | 'bottom' | 'left' | 'right'
  button?: {
    text: string
    onClick: (args: {
      /** Moves the slideshow to the next image */
      next: () => void
      /** Moves the slideshow to the previous image */
      previous: () => void
      /** Closes the modal (emits a 'close' event) */
      close: () => void
    }) => void
  }
}

const props = defineProps<{
  steps: Step[]
  autoSlideMillis?: number
  showCloseButton?: boolean
  width?: string
  textAlignment?: 'center' | 'left'
  showCheckbox?: boolean
  checkboxLabel?: string
}>()

const emit = defineEmits<{
  (e: 'close', checkbox: boolean): void
}>()

defineSlots<{
  text: (props: { text: string }) => void
}>()

const checkboxValue = ref(false)

const activeStepIndex = ref(0)

const sliderTimerId = ref<any>()

const activeStep = computed(() => props.steps[activeStepIndex.value])

const imageCssClass = computed(() => ({
  backgroundImage: `url(${activeStep.value.image})`,
}))

const modalCssVars = computed(() => ({
  '--slideshow-modal-width': props.width ?? '434px',
}))

const contentCssClass = computed(() => ({
  'text-center': !props.textAlignment || props.textAlignment === 'center',
  'text-left': props.textAlignment === 'left',
}))

function onNext() {
  activeStepIndex.value++

  if (activeStepIndex.value >= props.steps.length) {
    activeStepIndex.value = 0
  }
}

function onPrevious() {
  activeStepIndex.value--

  if (activeStepIndex.value < 0) {
    activeStepIndex.value = props.steps.length - 1
  }
}

function stepperStepClicked(stepIndex: number) {
  stopSliderTimer()

  activeStepIndex.value = stepIndex
}

function stopSliderTimer() {
  clearInterval(sliderTimerId.value)
}

function startSliderTimer() {
  if (props.autoSlideMillis) {
    sliderTimerId.value = setInterval(onNext, props.autoSlideMillis)
  }
}

function onPrimaryButtonClick() {
  activeStep.value.button?.onClick({
    previous: () => onPrevious(),
    next: () => onNext(),
    close: () => emit('close', checkboxValue.value),
  })
}

onMounted(() => {
  startSliderTimer()
})

onUnmounted(() => {
  stopSliderTimer()
})
</script>

<template>
  <div>
    <ModalBackdrop>
      <div
        class="flex flex-col justify-between static z-20 shadow-md bg-neutral-0 w-full md:max-w-[var(--slideshow-modal-width)] h-full md:h-auto md:rounded-lg"
        :style="modalCssVars"
      >
        <!-- Image -->
        <div
          class="bg-neutral-200 w-full h-[300px] rounded-t-lg border border-neutral-300 bg-cover bg-center object-contain"
          :class="{
            'bg-center':
              !activeStep.imagePosition ||
              activeStep.imagePosition === 'center',
            'bg-top': activeStep.imagePosition === 'top',
            'bg-bottom': activeStep.imagePosition === 'bottom',
            'bg-left': activeStep.imagePosition === 'left',
            'bg-right': activeStep.imagePosition === 'right',
          }"
          :style="imageCssClass"
        >
          <div v-if="showCloseButton" class="flex flex-row-reverse m-1">
            <AppButtonCircular
              icon-name="Close"
              class="bg-neutral-0 ml-auto"
              data-test="modal-close-button"
              @click="emit('close', checkboxValue)"
            />
          </div>
        </div>

        <!-- Content -->
        <div class="p-6" :class="contentCssClass">
          <!-- Title -->
          <span
            data-test="modal-title"
            class="self-center text-primary-800 font-semibold text-md mb-2"
          >
            {{ activeStep.title }}
          </span>

          <!-- Text -->
          <div
            class="mb-6 text-neutral-700 text-sm font-regular"
            data-test="modal-text"
          >
            <slot name="text" v-bind="{ text: activeStep.text }">
              <div v-html="parseMarkdown(activeStep.text)"></div>
            </slot>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <!-- Stepper -->
            <Stepper
              :steps="steps.length"
              :active-step-index="activeStepIndex"
              :class="{
                'col-span-2 justify-center': showCheckbox,
              }"
              @stepClick="stepperStepClicked"
            />

            <!-- Checkbox -->
            <label v-if="showCheckbox" class="flex gap-2 items-center ml-2">
              <Checkbox v-model="checkboxValue" class="shrink-0" />

              <span> {{ checkboxLabel }} </span>
            </label>

            <!-- Button -->
            <AppButton
              v-if="activeStep.button"
              class="primary text-sm"
              data-test="modal-primary-button"
              @click="onPrimaryButtonClick"
            >
              {{ activeStep.button.text }}
            </AppButton>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  </div>
</template>
